import { Box, IconButton } from "@mui/material"
import { MoreVertOutlined } from "@mui-symbols-material/w300"
import type { CellContext } from "@tanstack/react-table"
import { type FC, type MouseEvent, useRef, useState } from "react"

import { ConfirmationDialog } from "@/components/common/Modals"
import type { SignedUrl } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { ProductFileRowDropdownMenu } from "@/screens/Products/components/Product/Information/ProductFiles/ProductFileTable/ProductFileRowDropdownMenu"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface ProductFileRowActionsProps {
  row: CellContext<SignedUrl, unknown>["row"]
  onRemove: (fileId: string) => Promise<boolean>
}

export const ProductFileRowActions: FC<ProductFileRowActionsProps> = ({ row, onRemove }) => {
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [openedDialog, setOpenedDialog] = useState(false)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true)
  }
  const handleDropdownClose = () => {
    setIsDropdownOpen(false)
  }

  const handleStopPropagation = (event: MouseEvent<HTMLElement>) => event.stopPropagation()

  const handleViewDocument = () => {
    window.open(row.original.url, "_blank")
  }

  const handleRemoveDocument = async () => {
    const onRemoveResult = await onRemove(row.original?.file?.fileId as string)
    if (!onRemoveResult) {
      notify({
        message: "Product file removing was failed",
        type: NotificationType.Error,
      })
    } else {
      notify({
        message: "Product file was removed successfully",
        type: NotificationType.Success,
      })
    }

    setOpenedDialog(false)
  }

  return (
    <Box className='flex items-center justify-center' ref={anchorElRef} onClick={handleStopPropagation}>
      <IconButton onClick={handleDropdownOpen} data-testid='product-file-row-actions-button'>
        <MoreVertOutlined />
      </IconButton>
      <ProductFileRowDropdownMenu
        anchorEl={anchorElRef.current}
        isOpen={isDropdownOpen}
        onClose={handleDropdownClose}
        onViewDocument={handleViewDocument}
        onRemoveDocument={() => setOpenedDialog(true)}
      />
      {openedDialog && (
        <ConfirmationDialog
          title='Remove File'
          content='Are you sure you want to remove this file?'
          closeLabel='Cancel'
          confirmLabel='Yes, Remove'
          onClose={() => setOpenedDialog(false)}
          onConfirm={handleRemoveDocument}
        />
      )}
    </Box>
  )
}
